/**
 * Kontakty tab strip — the one list shared by KontaktyList, PlanovaneList,
 * StatistikyPage and KontaktySpravaPage. 'sprava' (the change history) is
 * shown only to kontakty admins once membership resolves — the API
 * (`/v1/historie/kontakty`, `kontakty:admin`) is the boundary.
 */

import { useMemo } from 'react';
import type { useNavigate } from '@tanstack/react-router';
import { useModuleAccess } from '@/lib/permissions';
import type { ContactType } from './api';

export type KontaktyTab = ContactType | 'planovane' | 'statistiky' | 'sprava';

const KONTAKTY_TAB_OPTIONS: { value: KontaktyTab; label: string }[] = [
  { value: 'rozjednane', label: 'Rozjednané' },
  { value: 'ukoncene', label: 'Ukončené' },
  { value: 'vsechny', label: 'Všechny' },
  { value: 'planovane', label: 'Plánované' },
  { value: 'statistiky', label: 'Statistiky' },
  { value: 'sprava', label: 'Správa' },
];

export function useKontaktyTabs(): { value: KontaktyTab; label: string }[] {
  const { can, resolved } = useModuleAccess();
  const canAdmin = resolved && can('kontakty', 'admin');
  return useMemo(
    () => KONTAKTY_TAB_OPTIONS.filter((o) => o.value !== 'sprava' || canAdmin),
    [canAdmin],
  );
}

/** Navigate to a tab's page; the list types land on `/kontakty?type=`. */
export function goToKontaktyTab(navigate: ReturnType<typeof useNavigate>, next: KontaktyTab): void {
  if (next === 'planovane') navigate({ to: '/kontakty/planovane' });
  else if (next === 'statistiky') navigate({ to: '/kontakty/statistiky' });
  else if (next === 'sprava') navigate({ to: '/kontakty/sprava' });
  else navigate({ to: '/kontakty', search: { type: next === 'rozjednane' ? undefined : next } });
}
